import { useState, useEffect } from 'react';
import { 
  CreditCard, Calendar, ArrowUpRight, ArrowDownLeft, Filter,
  Receipt, Loader2, AlertCircle, CheckCircle, Clock, XCircle,
  Wallet, Building2, Plus, ChevronDown, Send, ChevronLeft, ChevronRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { useRegion } from '../context/RegionContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Payments() {
  const { language } = useLanguage();
  const { isDarkMode } = useTheme();
  const { formatPrice, currency } = useRegion();
  const [loading, setLoading] = useState(true);
  const [payments, setPayments] = useState([]);
  const [filter, setFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 5;
  const [statusFilter, setStatusFilter] = useState('all'); // all|pending|completed|rejected
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [totalWithdrawn, setTotalWithdrawn] = useState(0);
  const [totalPaidFromWallet, setTotalPaidFromWallet] = useState(0);  // iter-308
  const [availableBalance, setAvailableBalance] = useState(0);
  const [unpaidInvoices, setUnpaidInvoices] = useState([]);   // iter-231
  const [unpaidTotal, setUnpaidTotal] = useState(0);          // iter-231
  const [partialTotal, setPartialTotal] = useState(0);        // iter-231
  const [payingInvoiceId, setPayingInvoiceId] = useState(null); // iter-231
  const [bankAccounts, setBankAccounts] = useState([]);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawing, setWithdrawing] = useState(false);
  const [showAccountDropdown, setShowAccountDropdown] = useState(false);

  useEffect(() => {
    fetchPaymentsData();
  }, []);

  // iter-302: Reset to first page when filter/status changes.
  useEffect(() => {
    setCurrentPage(1);
  }, [filter, statusFilter]);

  const fetchPaymentsData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('business_token');

      // NEW: aggregated agent earnings (commissions + revenue log from service_requests)
      const earningsRes = await axios.get(`${BACKEND_URL}/api/agent/earnings-summary`, {
        headers: { Authorization: `Bearer ${token}` }
      }).catch(() => ({ data: null }));

      // Legacy transactions (used to feed the withdrawals list; commissions come from earningsRes)
      const paymentsRes = await axios.get(`${BACKEND_URL}/api/payments/transactions`, {
        headers: { Authorization: `Bearer ${token}` }
      }).catch(() => ({ data: { transactions: [] } }));

      const accountsRes = await axios.get(`${BACKEND_URL}/api/users/bank-accounts`, {
        headers: { Authorization: `Bearer ${token}` }
      }).catch(() => ({ data: [] }));

      // iter-231: historical invoices from ASB606 (unpaid + partial + paid)
      const invoicesRes = await axios.get(`${BACKEND_URL}/api/invoices/mine`, {
        headers: { Authorization: `Bearer ${token}` }
      }).catch(() => ({ data: { invoices: [], totals: {} } }));

      // iter-250: NEW agent_invoices collection (written by ASB606 admin).
      // These are the "المبالغ المستحقة" invoices.
      const agentInvoicesRes = await axios.get(`${BACKEND_URL}/api/agent-invoices/mine`, {
        headers: { Authorization: `Bearer ${token}` }
      }).catch(() => ({ data: { invoices: [], totals: {} } }));

      // Build merged transaction list: commissions (income) + withdrawals (from legacy tx)
      const commissionTxs = (earningsRes.data?.revenue_log || []).map(r => ({
        id: r.id,
        type: 'income',
        amount: r.amount,
        percent: r.percent,
        description: r.service_name_ar || r.service_name_en || '',
        description_en: r.service_name_en || r.service_name_ar || '',
        request_no: r.request_no,
        status: 'completed',
        createdAt: r.date,
      }));
      const withdrawalTxs = (paymentsRes.data?.transactions || []).filter(t => t.type === 'withdrawal');
      // iter-308: wallet-paid invoice settlements as their own row type.
      const walletPaymentTxs = (paymentsRes.data?.transactions || []).filter(t => t.type === 'wallet_payment');

      const merged = [...commissionTxs, ...withdrawalTxs, ...walletPaymentTxs].sort((a, b) => {
        const da = new Date(a.createdAt || a.date || 0).getTime();
        const dbb = new Date(b.createdAt || b.date || 0).getTime();
        return dbb - da;
      });

      setPayments(merged);
      setTotalRevenue(earningsRes.data?.total_commissions ?? 0);
      setTotalWithdrawn(earningsRes.data?.withdrawn ?? 0);
      // iter-308: wallet-paid invoice settlements are a separate stat.
      setTotalPaidFromWallet(earningsRes.data?.paid_from_wallet ?? 0);
      // iter-206 safety: clamp to 0 so a stale/negative server response never
      // renders as a negative available balance in the UI.
      const bal = earningsRes.data?.available_balance ?? 0;
      setAvailableBalance(bal < 0 ? 0 : bal);
      setBankAccounts(accountsRes.data || []);

      // iter-231: split invoices by state; unpaid + partial block withdrawals.
      const invList = (invoicesRes.data?.invoices || []);
      const invTotals = invoicesRes.data?.totals || {};
      const outstanding = invList.filter(i => {
        const s = (i.payment_status || '').toLowerCase();
        return s === 'unpaid' || s === 'partial';
      });

      // iter-250: merge in agent_invoices from ASB606 admin. Normalize the
      // shape so the existing "outstanding" UI can render both sources.
      // iter-254 — Admin's canonical amount field is `total`. Backend now
      // fills in `remaining_amount` (= total when unpaid) & `total_amount`,
      // but we ALSO fall back to `i.total` here so a stale backend never
      // silently shows 0.00 AED again.
      // iter-285 — Per ASB606, service names are now guaranteed on
      // `service_type_ar` / `service_type_en` (backfilled from
      // `description` when the source was empty). We pick the field
      // that matches the current UI language so an EN user sees the
      // English name and an AR user sees the Arabic name — falling
      // back to `description` remains as a defensive last resort.
      const svcLabel = (s) => (
        language === 'ar'
          ? (s.service_type_ar || s.service_type_en || s.description)
          : (s.service_type_en || s.service_type_ar || s.description)
      );
      const agentInvList = (agentInvoicesRes.data?.invoices || []);
      const agentTotals = agentInvoicesRes.data?.totals || {};
      const agentOutstanding = agentInvList
        .filter(i => i.is_due)
        .map(i => ({
          id: i.id,
          invoice_no: i.invoice_no_display || i.invoice_no,
          amount_due: Number(i.remaining_amount ?? i.total_amount ?? i.total ?? 0),
          amount_total: Number(i.total_amount ?? i.total ?? 0),
          currency: i.currency || 'AED',
          payment_status: (i.status || '').toLowerCase().replace('partially_paid', 'partial'),
          description: (i.services || []).map(svcLabel).filter(Boolean).join(' · '),
          checkout_url: i.checkout_url || null,
          created_at: i.created_at,
          source: 'agent_invoices',
        }));

      const allOutstanding = [...outstanding, ...agentOutstanding];
      setUnpaidInvoices(allOutstanding);
      setUnpaidTotal(Number(invTotals.unpaid || 0) + Number(agentTotals.due || 0));
      setPartialTotal(Number(invTotals.partial || 0));
    } catch (error) {
      console.error('Error fetching payments data:', error);
    } finally {
      setLoading(false);
    }
  };

  const MIN_WITHDRAWAL = 100;

  const handleWithdrawRequest = async () => {
    if (!selectedAccount) {
      toast.error(language === 'ar' ? 'يرجى اختيار حساب بنكي' : 'Please select a bank account');
      return;
    }

    const amount = parseFloat(withdrawAmount);
    if (!amount || amount <= 0) {
      toast.error(language === 'ar' ? 'يرجى إدخال مبلغ صحيح' : 'Please enter a valid amount');
      return;
    }

    if (amount < MIN_WITHDRAWAL) {
      toast.error(language === 'ar'
        ? `الحد الأدنى للسحب هو ${MIN_WITHDRAWAL} درهم`
        : `Minimum withdrawal is AED ${MIN_WITHDRAWAL}`);
      return;
    }

    if (amount > availableBalance) {
      toast.error(language === 'ar' ? 'المبلغ أكبر من رصيد المحفظة' : 'Amount exceeds wallet balance');
      return;
    }

    try {
      setWithdrawing(true);
      const token = localStorage.getItem('business_token');
      
      await axios.post(`${BACKEND_URL}/api/payments/withdraw-request`, {
        amount,
        bankAccountId: selectedAccount._id,
        currency: currency.code
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(language === 'ar' ? 'تم إرسال طلب السحب بنجاح' : 'Withdrawal request submitted successfully');
      setShowWithdrawModal(false);
      setWithdrawAmount('');
      setSelectedAccount(null);
      fetchPaymentsData();
    } catch (error) {
      // iter-231: surface the "unpaid invoices block" server error as a clear
      // Arabic toast with the outstanding total; other errors get the generic msg.
      const detail = error?.response?.data?.detail;
      if (typeof detail === 'string' && detail.startsWith('UNPAID_INVOICES_BLOCK|')) {
        const [, amt, msg] = detail.split('|');
        toast.error(msg || (language === 'ar'
          ? `لديك فواتير معلقة بقيمة ${amt} — سدّدها أولاً من المحفظة`
          : `You have unpaid invoices of ${amt} — settle them from wallet first`));
      } else if (typeof detail === 'string' && detail === 'Insufficient balance') {
        toast.error(language === 'ar' ? 'المبلغ يتجاوز رصيد المحفظة' : 'Amount exceeds wallet balance');
      } else {
        toast.error(language === 'ar' ? 'حدث خطأ أثناء إرسال الطلب' : 'Error submitting request');
      }
    } finally {
      setWithdrawing(false);
    }
  };

  // iter-231: pay a single ASB606 invoice using the in-app wallet balance.
  const handlePayInvoiceFromWallet = async (invoice) => {
    const amt = Number(invoice.total_amount || 0);
    if (amt <= 0) return;
    if (amt > availableBalance) {
      toast.error(language === 'ar'
        ? `الرصيد غير كافٍ. تحتاج ${amt.toFixed(2)} درهم، رصيدك ${availableBalance.toFixed(2)}`
        : `Insufficient wallet. Need ${amt.toFixed(2)} AED, you have ${availableBalance.toFixed(2)}`);
      return;
    }
    const confirmMsg = language === 'ar'
      ? `سيتم خصم ${amt.toFixed(2)} درهم من محفظتك لتسديد الفاتورة ${invoice.invoice_number}. هل توافق؟`
      : `${amt.toFixed(2)} AED will be deducted from your wallet to settle invoice ${invoice.invoice_number}. Confirm?`;
    if (!window.confirm(confirmMsg)) return;

    try {
      setPayingInvoiceId(invoice.id);
      const token = localStorage.getItem('business_token');
      await axios.post(`${BACKEND_URL}/api/invoices/pay-from-wallet`,
        { invoice_id: invoice.id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(language === 'ar'
        ? `تم تسديد الفاتورة ${invoice.invoice_number} بنجاح`
        : `Invoice ${invoice.invoice_number} paid successfully`);
      fetchPaymentsData();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (typeof detail === 'string' && detail.startsWith('INSUFFICIENT_WALLET|')) {
        toast.error(language === 'ar' ? 'الرصيد غير كافٍ في المحفظة' : 'Insufficient wallet balance');
      } else {
        toast.error(language === 'ar' ? 'فشل تسديد الفاتورة' : 'Failed to pay invoice');
      }
    } finally {
      setPayingInvoiceId(null);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      completed: {
        bg: isDarkMode ? 'bg-green-500/20' : 'bg-green-100',
        text: 'text-green-500',
        icon: CheckCircle,
        label: language === 'ar' ? 'مكتمل' : 'Completed'
      },
      paid: {
        bg: isDarkMode ? 'bg-green-500/20' : 'bg-green-100',
        text: 'text-green-500',
        icon: CheckCircle,
        label: language === 'ar' ? 'مكتمل' : 'Completed'
      },
      // iter-228: once admin approves the withdrawal, the merchant sees it as
      // "تمت الموافقة والإرسال" (approved & sent) — treated as a completed state
      // in the UI. The internal `paid` status is still used for full financial
      // reconciliation but the merchant no longer waits on a separate step.
      approved: {
        bg: isDarkMode ? 'bg-green-500/20' : 'bg-green-100',
        text: 'text-green-500',
        icon: CheckCircle,
        label: language === 'ar' ? 'تمت الموافقة والإرسال' : 'Approved & Sent'
      },
      processing: {
        bg: isDarkMode ? 'bg-amber-500/20' : 'bg-amber-100',
        text: 'text-amber-500',
        icon: Clock,
        label: language === 'ar' ? 'قيد الإجراء' : 'In progress'
      },
      pending: {
        bg: isDarkMode ? 'bg-amber-500/20' : 'bg-amber-100',
        text: 'text-amber-500',
        icon: Clock,
        label: language === 'ar' ? 'قيد الإجراء' : 'In progress'
      },
      failed: {
        bg: isDarkMode ? 'bg-red-500/20' : 'bg-red-100',
        text: 'text-red-500',
        icon: XCircle,
        label: language === 'ar' ? 'مرفوض' : 'Rejected'
      },
      rejected: {
        bg: isDarkMode ? 'bg-red-500/20' : 'bg-red-100',
        text: 'text-red-500',
        icon: XCircle,
        label: language === 'ar' ? 'مرفوض' : 'Rejected'
      }
    };
    const style = styles[(status || '').toLowerCase()] || styles.pending;
    const Icon = style.icon;
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${style.bg} ${style.text}`}>
        <Icon className="h-3 w-3" />
        {style.label}
      </span>
    );
  };

  const filteredPayments = payments.filter(p => {
    if (filter === 'income' && p.type !== 'income') return false;
    if (filter === 'withdrawal' && p.type !== 'withdrawal') return false;
    if (filter === 'wallet_payment' && p.type !== 'wallet_payment') return false;
    // Status filter applies only to withdrawals; income rows always shown when not filtered out above.
    if (p.type === 'withdrawal' && statusFilter !== 'all') {
      const s = (p.status || '').toLowerCase();
      // iter-228: `approved` is surfaced as a completed state to the merchant.
      if (statusFilter === 'pending' && !(s === 'pending' || s === 'processing')) return false;
      if (statusFilter === 'completed' && !(s === 'completed' || s === 'paid' || s === 'approved')) return false;
      if (statusFilter === 'rejected' && !(s === 'rejected' || s === 'failed')) return false;
    }
    return true;
  });

  // Withdrawal-only counts per status (for chip badges)
  const wdCounts = payments.reduce((a, p) => {
    if (p.type !== 'withdrawal') return a;
    const s = (p.status || '').toLowerCase();
    a.all += 1;
    if (s === 'pending' || s === 'processing') a.pending += 1;
    else if (s === 'completed' || s === 'paid' || s === 'approved') a.completed += 1;
    else if (s === 'rejected' || s === 'failed') a.rejected += 1;
    return a;
  }, { all: 0, pending: 0, completed: 0, rejected: 0 });

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center cgm-page`}>
        <div className="relative">
          <div className="w-16 h-16 rounded-full border-4 border-[#38BDF8]/20 border-t-[#38BDF8] animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen py-6 px-4 cgm-page`}>
      {/* Background Effects */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className={`absolute top-20 start-10 w-[300px] h-[300px] rounded-full blur-[100px] ${isDarkMode ? 'bg-[#38BDF8]/10' : 'bg-[#38BDF8]/5'}`} />
        <div className={`absolute bottom-20 end-10 w-[250px] h-[250px] rounded-full blur-[80px] ${isDarkMode ? 'bg-[#F97316]/10' : 'bg-[#F97316]/5'}`} />
      </div>
      
      <div className="relative max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-5 cgm-ai-card-elevated cgm-reveal">
          <div className="flex items-center gap-3">
            <div className="cgm-icon-glow cgm-breathe !p-2.5">
              <Wallet className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-black cgm-text-gradient-animated" style={{ fontFamily: 'Cairo, sans-serif' }}>
                {language === 'ar' ? 'الأموال' : 'Money'}
              </h1>
              <p className={`text-sm mt-0.5 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                {language === 'ar' ? 'إدارة إيراداتك وطلبات السحب' : 'Manage your revenue and withdrawal requests'}
              </p>
            </div>
          </div>
        </div>

        {/* iter-231: Wallet card ALONE at the top (with inline withdraw button) */}
        <div className="cgm-ai-card-featured mb-4 flex items-center justify-between gap-4 cgm-reveal" data-testid="wallet-card">
          <div className="flex items-center gap-3 min-w-0">
            <div className="cgm-icon-glow !p-3 flex-shrink-0 cgm-breathe">
              <Wallet className="h-6 w-6 text-white" />
            </div>
            <div className="min-w-0">
              <p className={`text-xs font-bold uppercase tracking-wider ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                {language === 'ar' ? 'المحفظة' : 'Wallet'}
              </p>
              <p className="cgm-stat-value-ai mt-1">
                {formatPrice(availableBalance)}
              </p>
              <p className={`text-xs mt-1 ${isDarkMode ? 'text-slate-500' : 'text-slate-500'}`}>
                {language === 'ar'
                  ? 'الرصيد المتاح بعد المسحوبات والفواتير المعلقة'
                  : 'Available after withdrawals & pending invoices'}
              </p>
            </div>
          </div>
          <button
            onClick={() => setShowWithdrawModal(true)}
            disabled={availableBalance <= 0 || (unpaidTotal + partialTotal) > 0.005}
            className="cgm-cta-primary flex-shrink-0 disabled:opacity-50 disabled:pointer-events-none"
            data-testid="withdraw-request-btn"
          >
            <Send className="h-4 w-4" />
            {language === 'ar' ? 'طلب سحب' : 'Request'}
          </button>
        </div>

        {/* iter-309: 2×2 stats grid (Revenue, Withdrawn, Paid-from-wallet, Unpaid) */}
        <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-4 cgm-reveal-stagger">
          {/* Total Revenue */}
          <div className="cgm-ai-card" data-testid="total-revenue-card">
            <div className="flex items-center gap-3 mb-3">
              <div className="cgm-icon-glow !bg-gradient-to-br !from-green-500 !to-emerald-600 !p-2.5 flex-shrink-0">
                <ArrowDownLeft className="h-5 w-5 text-white" />
              </div>
              <div className="min-w-0 flex-1">
                <p className={`text-xs font-bold uppercase tracking-wider ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  {language === 'ar' ? 'إجمالي الإيرادات' : 'Total Revenue'}
                </p>
                <p className={`text-base sm:text-xl md:text-3xl font-black text-green-500 mt-1 truncate`} style={{ fontFamily: 'Cairo, sans-serif' }}>
                  {formatPrice(totalRevenue)}
                </p>
              </div>
            </div>
            <p className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-slate-500'}`}>
              {language === 'ar' ? 'إجمالي عمولاتك من جميع الطلبات' : 'Total commissions from all requests'}
            </p>
          </div>

          {/* Total Withdrawn */}
          <div className="cgm-ai-card" data-testid="total-withdrawn-card">
            <div className="flex items-center gap-3 mb-3">
              <div className="cgm-icon-glow !bg-gradient-to-br !from-amber-500 !to-orange-600 !p-2.5 flex-shrink-0">
                <ArrowUpRight className="h-5 w-5 text-white" />
              </div>
              <div className="min-w-0 flex-1">
                <p className={`text-xs font-bold uppercase tracking-wider ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  {language === 'ar' ? 'إجمالي المسحوبات' : 'Total Withdrawn'}
                </p>
                <p className={`text-base sm:text-xl md:text-3xl font-black text-amber-500 mt-1 truncate`} style={{ fontFamily: 'Cairo, sans-serif' }}>
                  {formatPrice(totalWithdrawn)}
                </p>
              </div>
            </div>
            <p className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-slate-500'}`}>
              {language === 'ar' ? 'مجموع السحوبات المكتملة' : 'Sum of completed withdrawals'}
            </p>
          </div>

          {/* iter-308: Total Paid from Wallet (invoice settlements) */}
          <div className="cgm-ai-card" data-testid="total-paid-from-wallet-card">
            <div className="flex items-center gap-3 mb-3">
              <div className="cgm-icon-glow !bg-gradient-to-br !from-sky-500 !to-cyan-600 !p-2.5 flex-shrink-0">
                <CheckCircle className="h-5 w-5 text-white" />
              </div>
              <div className="min-w-0 flex-1">
                <p className={`text-xs font-bold uppercase tracking-wider ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  {language === 'ar' ? 'المبالغ المدفوعة' : 'Amounts Paid'}
                </p>
                <p className={`text-base sm:text-xl md:text-3xl font-black text-sky-500 mt-1 truncate`} style={{ fontFamily: 'Cairo, sans-serif' }}>
                  {formatPrice(totalPaidFromWallet)}
                </p>
              </div>
            </div>
            <p className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-slate-500'}`}>
              {language === 'ar' ? 'الفواتير التي سدَّدتها من المحفظة' : 'Invoices settled from wallet'}
            </p>
          </div>

          {/* iter-231: Unpaid Invoices */}
          <div className="cgm-ai-card" data-testid="unpaid-invoices-card">
            <div className="flex items-center gap-3 mb-3">
              <div className="cgm-icon-glow !bg-gradient-to-br !from-rose-500 !to-red-600 !p-2.5 flex-shrink-0">
                <Receipt className="h-5 w-5 text-white" />
              </div>
              <div className="min-w-0 flex-1">
                <p className={`text-xs font-bold uppercase tracking-wider ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  {language === 'ar' ? 'المبالغ المستحقة' : 'Amounts Due'}
                </p>
                <p className={`text-base sm:text-xl md:text-3xl font-black text-rose-500 mt-1 truncate`} style={{ fontFamily: 'Cairo, sans-serif' }}>
                  {formatPrice(unpaidTotal + partialTotal)}
                </p>
              </div>
            </div>
            <p className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-slate-500'}`}>
              {language === 'ar'
                ? (unpaidInvoices.length > 0
                    ? `${unpaidInvoices.length} ${unpaidInvoices.length === 1 ? 'فاتورة مستحقة' : 'فواتير مستحقة'}`
                    : 'لا توجد فواتير مستحقة')
                : (unpaidInvoices.length > 0
                    ? `${unpaidInvoices.length} due ${unpaidInvoices.length === 1 ? 'invoice' : 'invoices'}`
                    : 'No amounts due')}
            </p>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className={`flex gap-2 mb-5 p-1.5 rounded-xl border ${
          isDarkMode 
            ? 'bg-[#131B2C]/80 border-white/10' 
            : 'bg-white/80 backdrop-blur-xl border-black/5 shadow-sm'
        }`}>
          {[
            { id: 'all', label: language === 'ar' ? 'الكل' : 'All' },
            { id: 'income', label: language === 'ar' ? 'الإيرادات' : 'Revenue' },
            { id: 'withdrawal', label: language === 'ar' ? 'السحوبات' : 'Withdrawals' },
            { id: 'wallet_payment', label: language === 'ar' ? 'المدفوعة' : 'Paid' },
            { id: 'unpaid', label: language === 'ar' ? 'المبالغ المستحقة' : 'Amounts Due' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setFilter(tab.id)}
              className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-all ${
                filter === tab.id
                  ? 'gradient-primary text-white shadow-lg shadow-[#38BDF8]/20'
                  : isDarkMode
                    ? 'text-gray-400 hover:text-white hover:bg-white/5'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* iter-302: Transactions History — always open, paginated (5 per page) */}
        <div className={`rounded-xl overflow-hidden border ${
          isDarkMode 
            ? 'bg-[#131B2C]/80 backdrop-blur-xl border-white/10' 
            : 'bg-white/80 backdrop-blur-xl border-black/5 shadow-sm'
        }`}>
          {/* Header — static, not clickable */}
          <div
            className={`w-full p-4 flex items-center justify-between gap-3 border-b ${
              isDarkMode ? 'border-white/10' : 'border-gray-100'
            }`}
            data-testid="tx-history-header"
          >
            <div className="flex items-center gap-2.5">
              <Receipt className={`h-4 w-4 ${isDarkMode ? 'text-[#38BDF8]' : 'text-[#0EA5E9]'}`} />
              <h2 className={`font-bold ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                {language === 'ar' ? 'سجل المعاملات' : 'Transaction History'}
              </h2>
              <span className={`text-[11px] px-2 py-0.5 rounded-md ${
                isDarkMode ? 'bg-white/10 text-gray-300' : 'bg-gray-100 text-gray-600'
              }`}>{filter === 'unpaid' ? unpaidInvoices.length : filteredPayments.length}</span>
            </div>
          </div>

          <>
              {/* Withdrawal status filter chips — only show when we have any withdrawal */}
              {wdCounts.all > 0 && (
                <div className={`p-3 flex flex-wrap items-center gap-2 border-b ${isDarkMode ? 'border-white/10 bg-white/[0.02]' : 'border-gray-100 bg-gray-50/60'}`}>
                  <span className={`text-[11px] font-semibold ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    {language === 'ar' ? 'حالة السحب:' : 'Withdrawal status:'}
                  </span>
                  {[
                    { id: 'all',       ar: 'الكل',       en: 'All',        color: 'neutral' },
                    { id: 'pending',   ar: 'قيد الإجراء', en: 'In Progress', color: 'amber'  },
                    { id: 'completed', ar: 'مكتمل',     en: 'Completed',   color: 'green'  },
                    { id: 'rejected',  ar: 'مرفوض',     en: 'Rejected',    color: 'red'    },
                  ].map(chip => {
                    const active = statusFilter === chip.id;
                    const count = wdCounts[chip.id];
                    const activeCls = chip.color === 'amber'
                      ? (isDarkMode ? 'bg-amber-500/25 text-amber-300 border-amber-400/50' : 'bg-amber-100 text-amber-800 border-amber-300')
                      : chip.color === 'green'
                      ? (isDarkMode ? 'bg-green-500/25 text-green-300 border-green-400/50' : 'bg-green-100 text-green-800 border-green-300')
                      : chip.color === 'red'
                      ? (isDarkMode ? 'bg-red-500/25 text-red-300 border-red-400/50' : 'bg-red-100 text-red-800 border-red-300')
                      : (isDarkMode ? 'bg-white/15 text-white border-white/20' : 'bg-gray-200 text-gray-900 border-gray-300');
                    const idleCls = isDarkMode
                      ? 'bg-white/5 text-gray-300 border-white/10 hover:bg-white/10'
                      : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-100';
                    return (
                      <button
                        key={chip.id}
                        type="button"
                        onClick={() => setStatusFilter(chip.id)}
                        data-testid={`wd-status-chip-${chip.id}`}
                        className={`text-[11px] px-2.5 py-1 rounded-full border transition ${active ? activeCls : idleCls}`}
                      >
                        {language === 'ar' ? chip.ar : chip.en}
                        <span className="ms-1 opacity-70">({count})</span>
                      </button>
                    );
                  })}
                </div>
              )}

              {filter === 'unpaid' ? (
                unpaidInvoices.length > 0 ? (
                  <div className={`divide-y ${isDarkMode ? 'divide-white/10' : 'divide-gray-100'}`}>
                    {unpaidInvoices
                      .slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE)
                      .map((inv) => {
                      // iter-250: unified rendering for legacy + agent_invoices.
                      const isAgentInv = inv.source === 'agent_invoices';
                      const amt = Number(inv.amount_due || inv.amount_total || inv.total_amount || 0);
                      const invNo = inv.invoice_no || inv.invoice_number;
                      const canPay = !isAgentInv && amt <= availableBalance && amt > 0;
                      const isProcessing = payingInvoiceId === inv.id;
                      const status = (inv.payment_status || 'unpaid').toLowerCase();
                      const statusLabel = status === 'partial'
                        ? (language === 'ar' ? 'مدفوعة جزئياً' : 'Partial')
                        : (language === 'ar' ? 'مستحقة' : 'Due');
                      return (
                        <div key={inv.id} className={`p-4 transition-colors ${isDarkMode ? 'hover:bg-white/5' : 'hover:bg-gray-50'}`} data-testid={`invoice-row-${inv.id}`}>
                          <div className="flex items-center justify-between gap-3 flex-wrap">
                            <div className="flex items-center gap-3 min-w-0 flex-1">
                              <div className="p-2 rounded-lg flex-shrink-0 bg-rose-500/20">
                                <Receipt className="h-4 w-4 text-rose-500" />
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className={`text-sm font-semibold truncate ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                                  {invNo}
                                </p>
                                <p className={`text-xs truncate ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                  {inv.description || inv.service_type || (language === 'ar' ? 'فاتورة خدمة' : 'Service invoice')}
                                </p>
                                <div className="flex items-center gap-2 mt-1 flex-wrap">
                                  <span className="text-[10px] text-gray-500">
                                    {new Date(inv.created_at).toLocaleDateString(language === 'ar' ? 'ar-AE' : 'en-US')}
                                  </span>
                                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${
                                    status === 'partial'
                                      ? (isDarkMode ? 'bg-amber-500/20 text-amber-400' : 'bg-amber-100 text-amber-700')
                                      : (isDarkMode ? 'bg-rose-500/20 text-rose-400' : 'bg-rose-100 text-rose-700')
                                  }`}>
                                    {statusLabel}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <div className="text-end">
                                <p className={`text-sm font-bold text-rose-500`}>
                                  {formatPrice(amt)}
                                </p>
                                {!isAgentInv && (
                                  <p className="text-[10px] text-gray-500">
                                    {language === 'ar' ? `شامل ضريبة ${inv.tax_percentage || 0}%` : `Incl. ${inv.tax_percentage || 0}% tax`}
                                  </p>
                                )}
                              </div>
                              {/* iter-293 — EVERY invoice (agent or merchant)
                                   now routes to the unified picker /pay/:id
                                   so users can pay via wallet OR any gateway
                                   (Stripe/Tamara/Ziina). The old wallet-only
                                   button bypassed external gateways. */}
                              <a
                                href={`/pay/${inv.id}`}
                                className="text-xs h-8 px-3 flex items-center gap-1 rounded-lg bg-gradient-to-r from-[#38BDF8] to-[#0EA5E9] hover:opacity-90 text-white flex-shrink-0"
                                data-testid={`checkout-invoice-${inv.id}`}
                              >
                                <Wallet className="h-3 w-3" />
                                {language === 'ar' ? 'ادفع الآن' : 'Pay now'}
                              </a>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="p-8 text-center">
                    <CheckCircle className={`h-12 w-12 mx-auto mb-3 text-green-500`} />
                    <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      {language === 'ar' ? 'لا توجد مبالغ مستحقة — أحسنت!' : 'No amounts due — all clear!'}
                    </p>
                  </div>
                )
              ) : filteredPayments.length > 0 ? (
                <div className={`divide-y ${isDarkMode ? 'divide-white/10' : 'divide-gray-100'}`}>
              {filteredPayments
                .slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE)
                .map((payment, index) => (
                <div key={index} className={`p-4 transition-colors ${isDarkMode ? 'hover:bg-white/5' : 'hover:bg-gray-50'}`} data-testid={`tx-row-${payment.id || index}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={`p-2 rounded-lg flex-shrink-0 ${
                        payment.type === 'income' ? 'bg-green-500/20' :
                        payment.type === 'wallet_payment' ? 'bg-sky-500/20' :
                        'bg-amber-500/20'
                      }`}>
                        {payment.type === 'income' ? (
                          <ArrowDownLeft className="h-5 w-5 text-green-500" />
                        ) : payment.type === 'wallet_payment' ? (
                          <CheckCircle className="h-5 w-5 text-sky-500" />
                        ) : (
                          <ArrowUpRight className="h-5 w-5 text-amber-500" />
                        )}
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                            {payment.type === 'wallet_payment'
                              ? (language === 'ar'
                                  ? (payment.description || `سداد فاتورة ${payment.invoice_number || ''}`)
                                  : (payment.description_en || `Invoice ${payment.invoice_number || ''} settlement`))
                              : (payment.description || (payment.type === 'income'
                                  ? (language === 'ar' ? 'عمولة طلب' : 'Request commission')
                                  : (language === 'ar' ? 'طلب سحب' : 'Withdrawal Request')
                                ))
                            }
                          </p>
                          {payment.type === 'income' && payment.percent != null && (
                            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-md border ${
                              isDarkMode ? 'bg-green-500/10 text-green-400 border-green-500/25' : 'bg-green-50 text-green-700 border-green-200'
                            }`} data-testid={`tx-percent-${payment.id || index}`}>
                              {payment.percent}%
                            </span>
                          )}
                          {payment.request_no && (
                            <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded-md ${isDarkMode ? 'bg-white/5 text-gray-400' : 'bg-gray-100 text-gray-600'}`}>
                              {payment.request_no}
                            </span>
                          )}
                          {payment.type === 'withdrawal' && payment.reference_number && (
                            <span className={`text-[10px] font-mono font-bold px-1.5 py-0.5 rounded-md border ${
                              isDarkMode ? 'bg-amber-500/10 text-amber-300 border-amber-500/25' : 'bg-amber-50 text-amber-700 border-amber-200'
                            }`} data-testid={`wd-ref-${payment.id || index}`}>
                              {payment.reference_number}
                            </span>
                          )}
                          {payment.type === 'wallet_payment' && payment.reference_number && (
                            <span className={`text-[10px] font-mono font-bold px-1.5 py-0.5 rounded-md border ${
                              isDarkMode ? 'bg-sky-500/10 text-sky-300 border-sky-500/25' : 'bg-sky-50 text-sky-700 border-sky-200'
                            }`} data-testid={`wp-ref-${payment.id || index}`}>
                              {payment.reference_number}
                            </span>
                          )}
                        </div>
                        <p className={`text-sm mt-0.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {new Date(payment.date || payment.createdAt).toLocaleDateString(language === 'ar' ? 'ar-AE' : 'en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                          {/* iter-247: badge shown when admin has edited
                              the withdrawal request date. Hover/click opens
                              the audit-trail history section below. */}
                          {payment.is_date_edited && (
                            <span
                              className={`ms-2 inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-md border align-middle ${
                                isDarkMode
                                  ? 'bg-sky-500/10 text-sky-300 border-sky-500/30'
                                  : 'bg-sky-50 text-sky-700 border-sky-200'
                              }`}
                              data-testid={`wd-date-edited-badge-${payment.id}`}
                              title={language === 'ar' ? 'تم تعديل تاريخ الطلب من قِبل الإدارة' : 'Request date edited by administration'}
                            >
                              {language === 'ar' ? 'عُدِّل بواسطة الإدارة' : 'Edited by admin'}
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                    <div className="text-end flex-shrink-0 flex flex-col items-end gap-1.5">
                      <p className={`font-bold ${
                        payment.type === 'income' ? 'text-green-500' :
                        payment.type === 'wallet_payment' ? 'text-sky-500' :
                        'text-amber-500'
                      }`}>
                        {payment.type === 'income' ? '+' : '-'}{formatPrice(payment.amount)}
                      </p>
                      {getStatusBadge(payment.status)}
                      {/* iter-308: download PDF receipt for wallet settlements */}
                      {payment.type === 'wallet_payment' && payment.reference_number && (
                        <a
                          href={`${BACKEND_URL}/api/payments/wallet-receipt/${encodeURIComponent(payment.reference_number)}?t=${localStorage.getItem('business_token') || ''}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={async (e) => {
                            // Use fetch so we can send the Authorization header.
                            e.preventDefault();
                            try {
                              const resp = await fetch(
                                `${BACKEND_URL}/api/payments/wallet-receipt/${encodeURIComponent(payment.reference_number)}`,
                                { headers: { Authorization: `Bearer ${localStorage.getItem('business_token')}` } }
                              );
                              if (!resp.ok) throw new Error('receipt-failed');
                              const blob = await resp.blob();
                              const url = URL.createObjectURL(blob);
                              window.open(url, '_blank');
                              setTimeout(() => URL.revokeObjectURL(url), 60000);
                            } catch {
                              toast.error(language === 'ar' ? 'تعذّر تحميل الإيصال' : 'Could not load receipt');
                            }
                          }}
                          className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-md bg-sky-500/15 text-sky-400 hover:bg-sky-500/25 transition-colors"
                          data-testid={`download-receipt-${payment.id || index}`}
                        >
                          <Receipt className="h-3 w-3" />
                          {language === 'ar' ? 'تحميل الإيصال' : 'Download receipt'}
                        </a>
                      )}
                    </div>
                  </div>

                  {/* iter-206: rejected withdrawal — show reason + admin notes */}
                  {payment.type === 'withdrawal' && payment.status === 'rejected' && (
                    <div className={`mt-3 rounded-lg border p-3 space-y-2 ${
                      isDarkMode ? 'bg-red-500/10 border-red-500/30' : 'bg-red-50 border-red-200'
                    }`} data-testid={`wd-rejection-banner-${payment.id}`}>
                      <div className={`flex items-center gap-1.5 font-semibold text-sm ${isDarkMode ? 'text-red-300' : 'text-red-700'}`}>
                        <XCircle className="h-4 w-4" />
                        {language === 'ar' ? 'تم رفض طلب السحب' : 'Withdrawal rejected'}
                      </div>
                      {(() => {
                        const reason = language === 'ar'
                          ? (payment.reason_ar || payment.reason || payment.reason_en)
                          : (payment.reason_en || payment.reason || payment.reason_ar);
                        return reason ? (
                          <p className={`text-sm ${isDarkMode ? 'text-red-100' : 'text-red-900'}`} data-testid={`wd-rejection-reason-${payment.id}`}>
                            {reason}
                          </p>
                        ) : null;
                      })()}
                      {payment.rejection_notes && (
                        <div className={`rounded p-2 border ${isDarkMode ? 'bg-red-500/5 border-red-500/20' : 'bg-white border-red-100'}`}>
                          <div className={`text-[10px] uppercase tracking-wide mb-0.5 ${isDarkMode ? 'text-red-300/70' : 'text-red-600'}`}>
                            {language === 'ar' ? 'ملاحظات الإدارة' : 'Admin notes'}
                          </div>
                          <p className={`text-[13px] whitespace-pre-wrap ${isDarkMode ? 'text-red-100/90' : 'text-red-800'}`} data-testid={`wd-rejection-notes-${payment.id}`}>
                            {payment.rejection_notes}
                          </p>
                        </div>
                      )}
                      {payment.processed_at && (
                        <div className={`text-[10px] ${isDarkMode ? 'text-red-300/60' : 'text-red-600/80'}`}>
                          {language === 'ar' ? 'تاريخ الرفض: ' : 'Rejected at: '}
                          {new Date(payment.processed_at).toLocaleString(language === 'ar' ? 'ar-AE' : 'en-GB')}
                        </div>
                      )}
                      <p className={`text-[11px] mt-1 ${isDarkMode ? 'text-emerald-300' : 'text-emerald-700'}`}>
                        {language === 'ar' ? '✓ تم إعادة المبلغ إلى محفظتك.' : '✓ Amount refunded to your wallet.'}
                      </p>
                    </div>
                  )}

                  {/* iter-247: Admin edited the withdrawal request date —
                      show full audit trail so the merchant can see the
                      original date, the new date, who edited it and why. */}
                  {payment.type === 'withdrawal' && payment.is_date_edited && (
                    <div
                      className={`mt-3 rounded-lg border p-3 space-y-2 ${
                        isDarkMode ? 'bg-sky-500/10 border-sky-500/30' : 'bg-sky-50 border-sky-200'
                      }`}
                      data-testid={`wd-date-edit-banner-${payment.id}`}
                    >
                      <div className={`flex items-center gap-1.5 font-semibold text-sm ${isDarkMode ? 'text-sky-300' : 'text-sky-700'}`}>
                        <Calendar className="h-4 w-4" />
                        {language === 'ar' ? 'تم تعديل تاريخ الطلب من قِبل الإدارة' : 'Request date edited by administration'}
                      </div>
                      {payment.original_created_at && (
                        <div className={`text-[12px] ${isDarkMode ? 'text-sky-200/80' : 'text-sky-800'}`}>
                          <span className="opacity-70">
                            {language === 'ar' ? 'التاريخ الأصلي: ' : 'Original date: '}
                          </span>
                          {new Date(payment.original_created_at).toLocaleString(language === 'ar' ? 'ar-AE' : 'en-GB')}
                        </div>
                      )}
                      {payment.date_edit_reason && (
                        <div className={`rounded p-2 border ${isDarkMode ? 'bg-sky-500/5 border-sky-500/20' : 'bg-white border-sky-100'}`}>
                          <div className={`text-[10px] uppercase tracking-wide mb-0.5 ${isDarkMode ? 'text-sky-300/70' : 'text-sky-600'}`}>
                            {language === 'ar' ? 'سبب التعديل' : 'Reason'}
                          </div>
                          <p
                            className={`text-[13px] whitespace-pre-wrap ${isDarkMode ? 'text-sky-100/90' : 'text-sky-900'}`}
                            data-testid={`wd-date-edit-reason-${payment.id}`}
                          >
                            {payment.date_edit_reason}
                          </p>
                        </div>
                      )}
                      {payment.date_edited_at && (
                        <div className={`text-[10px] ${isDarkMode ? 'text-sky-300/60' : 'text-sky-600/80'}`}>
                          {language === 'ar' ? 'تاريخ التعديل: ' : 'Edited at: '}
                          {new Date(payment.date_edited_at).toLocaleString(language === 'ar' ? 'ar-AE' : 'en-GB')}
                          {payment.date_edited_by && (
                            <> · {payment.date_edited_by}</>
                          )}
                        </div>
                      )}
                      {Array.isArray(payment.date_edit_history) && payment.date_edit_history.length > 1 && (
                        <details className="mt-1">
                          <summary className={`text-[11px] cursor-pointer ${isDarkMode ? 'text-sky-300/80 hover:text-sky-200' : 'text-sky-700 hover:text-sky-900'}`}>
                            {language === 'ar'
                              ? `عرض السجل الكامل (${payment.date_edit_history.length})`
                              : `Show full history (${payment.date_edit_history.length})`}
                          </summary>
                          <ul className={`mt-1.5 ps-3 space-y-1 text-[11px] ${isDarkMode ? 'text-sky-200/80' : 'text-sky-800'}`}>
                            {payment.date_edit_history.map((h, hi) => (
                              <li key={hi} className="list-disc list-outside">
                                {h.previous_value && (
                                  <>
                                    <span className="opacity-70">{language === 'ar' ? 'من ' : 'from '}</span>
                                    {new Date(h.previous_value).toLocaleString(language === 'ar' ? 'ar-AE' : 'en-GB')}
                                  </>
                                )}
                                {h.new_value && (
                                  <>
                                    <span className="opacity-70">{language === 'ar' ? ' إلى ' : ' to '}</span>
                                    {new Date(h.new_value).toLocaleString(language === 'ar' ? 'ar-AE' : 'en-GB')}
                                  </>
                                )}
                                {h.edited_by && <> · {h.edited_by}</>}
                                {h.reason && <> — {h.reason}</>}
                              </li>
                            ))}
                          </ul>
                        </details>
                      )}
                    </div>
                  )}

                  {/* Completed withdrawal — show transfer reference + receipt */}
                  {payment.type === 'withdrawal' && ['completed', 'paid'].includes((payment.status || '').toLowerCase()) && (payment.transfer_reference || payment.receipt_url) && (
                    <div className={`mt-3 rounded-lg border p-3 flex items-center gap-3 flex-wrap ${
                      isDarkMode ? 'bg-emerald-500/10 border-emerald-500/25' : 'bg-emerald-50 border-emerald-200'
                    }`}>
                      {payment.transfer_reference && (
                        <span className={`text-xs font-mono ${isDarkMode ? 'text-emerald-200' : 'text-emerald-800'}`}>
                          {language === 'ar' ? 'رقم الحوالة: ' : 'Ref: '}{payment.transfer_reference}
                        </span>
                      )}
                      {payment.receipt_url && (
                        <a href={payment.receipt_url} target="_blank" rel="noopener noreferrer"
                          className={`text-xs font-semibold underline ${isDarkMode ? 'text-emerald-300' : 'text-emerald-700'}`}
                          data-testid={`wd-receipt-link-${payment.id}`}>
                          {language === 'ar' ? 'تحميل الإيصال' : 'Download receipt'}
                        </a>
                      )}
                    </div>
                  )}

                  {/* iter-207: pending withdrawal — SLA note (15 business days) */}
                  {payment.type === 'withdrawal' && payment.status === 'pending' && (
                    <div className={`mt-3 rounded-lg border p-3 flex items-center gap-2 ${
                      isDarkMode ? 'bg-amber-500/10 border-amber-500/25' : 'bg-amber-50 border-amber-200'
                    }`} data-testid={`wd-sla-note-${payment.id}`}>
                      <Clock className={`h-4 w-4 flex-shrink-0 ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`} />
                      <p className={`text-xs font-medium ${isDarkMode ? 'text-amber-200' : 'text-amber-800'}`}>
                        {language === 'ar'
                          ? 'قيد المراجعة — يتم صرف السحب خلال 15 يوم عمل من تاريخ تقديم الطلب.'
                          : 'Under review — payout is processed within 15 business days of request submission.'}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="py-16 text-center">
              <Receipt className={`h-16 w-16 mx-auto mb-4 ${isDarkMode ? 'text-gray-600' : 'text-gray-300'}`} />
              <p className={`text-lg font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                {language === 'ar' ? 'لا توجد معاملات' : 'No transactions yet'}
              </p>
              <p className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                {language === 'ar' ? 'ستظهر معاملاتك المالية هنا' : 'Your financial transactions will appear here'}
              </p>
            </div>
          )}

          {/* iter-302 — Numeric pagination (max 5 visible pages) at the bottom.
              Shown whenever the current filtered list has more than PAGE_SIZE items.
              Covers both the "unpaid invoices" view and the regular transactions. */}
          {(() => {
            const totalItems = filter === 'unpaid' ? unpaidInvoices.length : filteredPayments.length;
            const totalPages = Math.max(1, Math.ceil(totalItems / PAGE_SIZE));
            if (totalPages <= 1) return null;
            // 5-page sliding window centered on currentPage, clamped to [1, totalPages]
            const WINDOW = 5;
            let start = Math.max(1, currentPage - Math.floor(WINDOW / 2));
            let end = Math.min(totalPages, start + WINDOW - 1);
            start = Math.max(1, end - WINDOW + 1);
            const pages = Array.from({ length: end - start + 1 }, (_, i) => start + i);
            return (
              <div
                className={`flex items-center justify-center gap-1.5 py-4 px-3 border-t ${
                  isDarkMode ? 'border-white/10 bg-white/[0.02]' : 'border-gray-100 bg-gray-50/60'
                }`}
                data-testid="tx-pagination"
              >
                <button
                  type="button"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className={`h-8 w-8 flex items-center justify-center rounded-lg border text-sm transition ${
                    currentPage === 1
                      ? (isDarkMode ? 'border-white/5 text-gray-600 cursor-not-allowed' : 'border-gray-200 text-gray-300 cursor-not-allowed')
                      : (isDarkMode ? 'border-white/10 text-gray-300 hover:bg-white/5' : 'border-gray-200 text-gray-700 hover:bg-white')
                  }`}
                  data-testid="tx-page-prev"
                  aria-label={language === 'ar' ? 'السابق' : 'Previous'}
                >
                  {language === 'ar' ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
                </button>
                {pages.map(p => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setCurrentPage(p)}
                    className={`h-8 min-w-[2rem] px-2 flex items-center justify-center rounded-lg border text-sm font-semibold transition ${
                      currentPage === p
                        ? 'gradient-primary text-white border-transparent shadow-md shadow-[#38BDF8]/20'
                        : (isDarkMode
                            ? 'border-white/10 text-gray-300 hover:bg-white/5'
                            : 'border-gray-200 text-gray-700 hover:bg-white')
                    }`}
                    data-testid={`tx-page-${p}`}
                    aria-current={currentPage === p ? 'page' : undefined}
                  >
                    {p}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className={`h-8 w-8 flex items-center justify-center rounded-lg border text-sm transition ${
                    currentPage === totalPages
                      ? (isDarkMode ? 'border-white/5 text-gray-600 cursor-not-allowed' : 'border-gray-200 text-gray-300 cursor-not-allowed')
                      : (isDarkMode ? 'border-white/10 text-gray-300 hover:bg-white/5' : 'border-gray-200 text-gray-700 hover:bg-white')
                  }`}
                  data-testid="tx-page-next"
                  aria-label={language === 'ar' ? 'التالي' : 'Next'}
                >
                  {language === 'ar' ? <ChevronLeft className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                </button>
              </div>
            );
          })()}
            </>
        </div>
      </div>

      {/* Withdraw Modal */}
      {showWithdrawModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className={`w-full max-w-md rounded-2xl p-6 ${
            isDarkMode ? 'bg-[#131B2C] border border-white/10' : 'bg-white shadow-xl'
          }`}>
            <h2 className={`text-xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
              {language === 'ar' ? 'طلب سحب الرصيد' : 'Request Withdrawal'}
            </h2>

            {/* Available Balance Display */}
            <div className={`p-4 rounded-xl mb-4 ${isDarkMode ? 'bg-white/5' : 'bg-gray-50'}`}>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {language === 'ar' ? 'رصيد المحفظة' : 'Wallet Balance'}
              </p>
              <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                {formatPrice(availableBalance)}
              </p>
            </div>

            {/* Amount Input */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-1">
                <Label className={isDarkMode ? 'text-gray-300' : ''}>
                  {language === 'ar' ? 'مبلغ السحب' : 'Withdrawal Amount'}
                </Label>
                {/* iter-224: "Full amount" quick-fill button.
                    - balance == 0     → "لا يوجد أموال" toast + no action
                    - balance < 100    → "المبلغ أقل من الحد الأدنى" toast + no action
                    - balance ≥ 100    → fills the input with the floored wallet total */}
                <button
                  type="button"
                  onClick={() => {
                    const bal = Number(availableBalance) || 0;
                    if (bal <= 0) {
                      toast.error(language === 'ar' ? 'لا يوجد أموال في المحفظة' : 'No funds available in wallet');
                      return;
                    }
                    if (bal < MIN_WITHDRAWAL) {
                      toast.error(language === 'ar'
                        ? `المبلغ أقل من الحد الأدنى (${MIN_WITHDRAWAL} درهم)`
                        : `Amount is below the minimum (AED ${MIN_WITHDRAWAL})`);
                      return;
                    }
                    // Floor to fils (2dp) — never round up beyond the wallet.
                    const floored = Math.floor(bal * 100 + 1e-6) / 100;
                    setWithdrawAmount(String(floored));
                  }}
                  className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-md border transition-colors ${
                    isDarkMode
                      ? 'text-[#38BDF8] border-[#38BDF8]/40 bg-[#38BDF8]/10 hover:bg-[#38BDF8]/20'
                      : 'text-[#0EA5E9] border-[#0EA5E9]/40 bg-[#0EA5E9]/10 hover:bg-[#0EA5E9]/20'
                  }`}
                  data-testid="withdraw-full-amount-btn"
                >
                  {language === 'ar' ? 'كامل المبلغ' : 'Full amount'}
                </button>
              </div>
              <Input
                type="number"
                value={withdrawAmount}
                onChange={(e) => setWithdrawAmount(e.target.value)}
                placeholder={language === 'ar' ? 'أدخل المبلغ' : 'Enter amount'}
                className={`mt-1 ${isDarkMode ? 'bg-[#0F172A] border-[#334155] text-white' : ''}`}
                data-testid="withdraw-amount-input"
              />
              <p className={`text-[11px] mt-1.5 ${isDarkMode ? 'text-amber-300/80' : 'text-amber-700'}`} data-testid="withdraw-min-note">
                {language === 'ar'
                  ? `⚠️ الحد الأدنى لعمليات السحب: ${MIN_WITHDRAWAL} درهم`
                  : `⚠️ Minimum withdrawal amount: AED ${MIN_WITHDRAWAL}`}
              </p>
            </div>

            {/* Bank Account Selection */}
            <div className="mb-4">
              <Label className={isDarkMode ? 'text-gray-300' : ''}>
                {language === 'ar' ? 'الحساب البنكي' : 'Bank Account'}
              </Label>
              <div className="relative mt-1">
                <button
                  onClick={() => setShowAccountDropdown(!showAccountDropdown)}
                  className={`w-full p-3 rounded-xl border flex items-center justify-between ${
                    isDarkMode 
                      ? 'bg-[#0F172A] border-[#334155] text-white' 
                      : 'bg-white border-gray-200 text-[#0F172A]'
                  }`}
                >
                  {selectedAccount ? (
                    <div className="flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-[#38BDF8]" />
                      <span>{selectedAccount.bankName} - ****{selectedAccount.accountNumber?.slice(-4)}</span>
                    </div>
                  ) : (
                    <span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>
                      {language === 'ar' ? 'اختر حساب بنكي' : 'Select bank account'}
                    </span>
                  )}
                  <ChevronDown className={`h-4 w-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                </button>

                {showAccountDropdown && (
                  <div className={`absolute top-full mt-1 w-full rounded-xl border z-10 overflow-hidden ${
                    isDarkMode 
                      ? 'bg-[#0F172A] border-[#334155]' 
                      : 'bg-white border-gray-200 shadow-lg'
                  }`}>
                    {bankAccounts.length > 0 ? (
                      bankAccounts.map((account) => (
                        <button
                          key={account._id}
                          onClick={() => {
                            setSelectedAccount(account);
                            setShowAccountDropdown(false);
                          }}
                          className={`w-full p-3 flex items-center gap-2 transition-colors ${
                            isDarkMode ? 'hover:bg-white/5' : 'hover:bg-gray-50'
                          }`}
                        >
                          <Building2 className="h-4 w-4 text-[#38BDF8]" />
                          <span className={isDarkMode ? 'text-white' : 'text-[#0F172A]'}>
                            {account.bankName} - ****{account.accountNumber?.slice(-4)}
                          </span>
                        </button>
                      ))
                    ) : null}
                    <Link
                      to="/account?section=bank-accounts"
                      className={`w-full p-3 flex items-center gap-2 border-t transition-colors ${
                        isDarkMode 
                          ? 'border-[#334155] hover:bg-white/5 text-[#38BDF8]' 
                          : 'border-gray-100 hover:bg-gray-50 text-[#1E9BD9]'
                      }`}
                    >
                      <Plus className="h-4 w-4" />
                      <span>{language === 'ar' ? 'إضافة حساب بنكي جديد' : 'Add new bank account'}</span>
                    </Link>
                  </div>
                )}
              </div>
            </div>

            {/* iter-207: SLA info banner shown to user before submitting */}
            <div className={`mb-4 rounded-lg border p-3 flex items-start gap-2 ${
              isDarkMode ? 'bg-blue-500/10 border-blue-500/25' : 'bg-blue-50 border-blue-200'
            }`} data-testid="withdraw-sla-banner">
              <Clock className={`h-4 w-4 mt-0.5 flex-shrink-0 ${isDarkMode ? 'text-blue-300' : 'text-blue-600'}`} />
              <p className={`text-xs leading-relaxed ${isDarkMode ? 'text-blue-100' : 'text-blue-800'}`}>
                {language === 'ar'
                  ? 'مدة صرف الطلب: خلال 15 يوم عمل من تاريخ تقديم الطلب. سيظهر طلبك في قسم "السحوبات" بحالة "قيد المراجعة" حتى تتم المعالجة.'
                  : 'Payout SLA: within 15 business days of submission. Your request will appear under "Withdrawals" as "Under review" until processed.'}
              </p>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <Button
                onClick={() => {
                  setShowWithdrawModal(false);
                  setWithdrawAmount('');
                  setSelectedAccount(null);
                }}
                variant="outline"
                className={`flex-1 ${isDarkMode ? 'border-[#334155] text-gray-300 hover:bg-white/5' : ''}`}
              >
                {language === 'ar' ? 'إلغاء' : 'Cancel'}
              </Button>
              <Button
                onClick={handleWithdrawRequest}
                disabled={withdrawing || !selectedAccount || !withdrawAmount}
                className="flex-1 bg-gradient-to-r from-[#38BDF8] to-[#0EA5E9] hover:opacity-90 text-white"
              >
                {withdrawing ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <Send className="h-4 w-4 me-2" />
                    {language === 'ar' ? 'إرسال الطلب' : 'Submit Request'}
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
